import 'whatwg-fetch';
import qs from 'query-string';
import { omitBy, isObject } from 'lodash';
import { getTabId } from 'utils/tabIdentifier';

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 * @param  {boolean} includeHeaders Bool whether to include response headers in json response
 * @param  {boolean} includeBlob Bool return response.blob() as well
 *
 * @return {object}          The parsed JSON from the request
 */
export function parseJSON(
  response,
  includeHeaders = false,
  includeBlob = false,
) {
  return new Promise((resolve) => {
    const conditionalParams = includeHeaders
      ? { headers: response.headers }
      : {};

    if (response.status === 204 || response.status === 205) {
      const returnObj = {
        status: response.status,
        ok: response.ok,
        json: null,
        ...conditionalParams,
      };

      if (includeBlob) {
        response
          .blob()
          .then((blobData) => resolve({ ...returnObj, blob: blobData }));
      } else {
        return resolve(returnObj);
      }
    }

    const returnObj = {
      status: response.status,
      ...conditionalParams,
    };

    const handleResponse = () =>
      response.text().then((json) => {
        let data = json;

        try {
          data = JSON.parse(json);
        } catch (ex) {
          console.debug('Could not parse JSON', {
            response,
            ex,
            returnObj,
            data,
          });
          return resolve({ ...returnObj, json: data, ok: false });
        }

        return resolve({ ...returnObj, json: data, ok: response.ok });
      });

    const handleBlob = (blob) => {
      returnObj.blob = blob;

      return response
        .text()
        .then((json) => resolve({ ...returnObj, json, ok: response.ok }));
    };

    if (!includeBlob) {
      return handleResponse();
    }

    return response
      .clone()
      .blob()
      .then((blobData) => handleBlob(blobData));
  });
}

function parseError(response, url, queryParams) {
  const error = {};
  error.status = response.status;
  error.statusText = response.statusText;
  error.body = response.json;
  error.url = url;
  error.queryParams = queryParams;
  return error;
}

function getQueryParams(params) {
  return qs.stringify({ ...params }, { sort: false });
}

export function shallowExcludeValues(object = {}, excludedValues = []) {
  const cleanObject = omitBy(object, (value) => excludedValues.includes(value));

  return cleanObject;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default function request(url, options = {}) {
  const {
    queryParams,
    includeHeaders = false,
    includeBlob = false,
    includeStatus = false,
    ...restOptions
  } = options;
  const requestOptions = {
    ...restOptions,
    credentials: 'same-origin',
    headers: { ...options.headers, 'X-Session-Identifier': getTabId() },
  };

  return new Promise((resolve, reject) => {
    let URL = url;
    if (queryParams) {
      URL = `${URL}?${getQueryParams(queryParams)}`;
    }
    fetch(URL, requestOptions)
      .then((res) => parseJSON(res, includeHeaders, includeBlob))
      .then((response) => {
        if (response.ok) {
          if (includeBlob || includeHeaders || includeStatus) {
            const conditionalUpdates = {};
            if (includeBlob) {
              conditionalUpdates.blob = response.blob;
            }
            if (includeHeaders) {
              conditionalUpdates.headers = response.headers;
            }

            if (includeStatus) {
              conditionalUpdates.status = response.status;
            }

            if (isObject(response.json)) {
              return resolve({
                ...response.json,
                ...conditionalUpdates,
              });
            }
            if (includeBlob) {
              return resolve({
                ...conditionalUpdates,
              });
            }

            console.error('Incompatible flags with request');
          }

          return resolve(response.json);
        }
        // extract the error from the server's json
        return reject(parseError(response, url, queryParams));
      })
      .catch(
        /* istanbul ignore next */ (error) =>
          // eslint-disable-next-line
          reject({
            status: 500,
            url,
            queryParams,
            error,
          }),
      );
  });
}
